import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  useGetDatasetDataMutation,
  useMutationDatasetMetadata
} from "@/api/dataset/useDatasetAPI";
import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import { useMutateGetWidgetDefaultConfig } from "@/api/widget/useWidgetAPI";
import AdminAreaSelection from "@/components/adminAreaSelection";
import NotificationContent from "@/components/notification/notificationContent";
import Divider from "@/components/ui/Divider";
import LimitWarning from "@/components/ui/LimitWarning";
import LoadingWrapper from "@/components/ui/LoadingWrapper";
import { MAX_POI } from "@/constants/dataset";
import { Timezone } from "@/constants/dateTime";
import { ERROR_MESSAGE, MESSAGE } from "@/constants/message";
import {
  getAdminAreaRequired,
  getAdminAreaVisibility
} from "@/helpers/datasetAttributeHelper";
import { useAttributeFilter } from "@/hooks/attributeFilter/useAttributeFilter";
import { useH3Configuration } from "@/hooks/h3Configuration/useH3Configuration";
import { useDataset } from "@/hooks/map/useDataset";
import { useExploreDatasetMocks } from "@/hooks/onboarding/explorer/useExploreDatasetMocks";
import { useSpatialInputRowsLimit } from "@/hooks/spatialAnalysis/useSpatialInputRowsLimit";
import { useConfirmationModal } from "@/hooks/ui/modal/useConfirmationModal";
import useLoadingAndToast from "@/hooks/ui/useLoadingAndToast";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { useDatasetStream } from "@/hooks/useDatasetStream";
import { RootState } from "@/store";
import { toggleUseGlobalFilter } from "@/store/slice/datasetSlice";
import { selectIsOnboardingActive } from "@/store/slice/onboardingSlice";
import {
  ESidebarMenuType,
  setLoadingOverlay,
  setSideBarMenuTypePopupDisplay
} from "@/store/slice/uiSlice";
import {
  DatasetData,
  DatasetDetailResponse,
  DatasetFilter,
  DatasetStatsCountStatusENUM
} from "@/types/dataset";
import { PostWidgetDefaultConfigResponse } from "@/types/widget";
import {
  convertDatasetResponseToFeatureCollection,
  isFeatureCollectionWGS84
} from "@/utils/dataset";

import { Button } from "equator-design-system";

import AggregationFilter from "./AggregationFilter";
import { AttributeFilter } from "./AttributeFilter";
import CustomFilterArea from "./CustomFilterArea";
import EmptyState from "./EmptyState";
import Header from "./Header";
import SavedAdmArea from "./SavedAdmArea";
import SavedFiltersList from "./SavedFiltersList";

const TEST_ID_PREFIX = "dataset-explorer-attribute-filter";

type DatasetRightAttrFilterProps = {
  disabled?: boolean;
  selectedDataset: DatasetDetailResponse | null;
  source?: string;
  isSubscribed?: boolean;
  isLoading?: boolean;
  onClose: () => void;
  totalRows?: number;
  datasetData?: DatasetData;
};

const CatchmentErrorMessage = ({ datasetLength = 0 }) => (
  <span className="mb-2 text-xs text-cinnabar">
    {datasetLength === 0
      ? "No data selected. Please select at least one record to proceed."
      : `Selection exceeds the limit (${datasetLength}/${MAX_POI}). Please reduce the number of records to proceed.`}
  </span>
);

const DatasetRightAttrFilter = ({
  disabled = false,
  selectedDataset,
  isSubscribed = true,
  isLoading = false,
  totalRows = 0,
  datasetData: defaultDatasetData,
  onClose
}: DatasetRightAttrFilterProps) => {
  const [isLoadingDataset, setIsLoadingDataset] = useState(false);
  // True when the stream reports over-limit (H3 case `/stats` can't catch
  // pre-flight). Shows the warning and disables "Add Dataset"; cleared when the
  // user changes the selection to retry.
  const [isShowHideLimitWarning, setShowHideLimitWarning] =
    useState<boolean>(false);
  const [datasetData, setDatasetData] = useState<DatasetData | undefined>();
  const [datasetStreamId, setDatasetStreamId] = useState<string | undefined>();
  const [groupByValue, setGroupByValue] = useState<string | undefined>();
  const [defaultWidgetData, setDefaultWidgetData] = useState<
    PostWidgetDefaultConfigResponse | undefined
  >(undefined);
  const shouldApplyGlobalAreaRef = useRef(false);

  const {
    isUseNewDatasetExplorerModal,
    isAdminTreeEnabled,
    isGlobalFilterOverrideEnabled,
    isBVTDataForCatchmentEnabled,
    isDatasetWidgetEnabled
  } = useAppFeatures();
  const dispatch = useDispatch();
  const { sideBarMenuTypePopupDisplay, showCatchmentPopup } = useSelector(
    (state: RootState) => state.ui
  );
  const { catchmentSettingsTemp } = useSelector(
    (state: RootState) => state.spatial
  );
  const { datasets } = useSelector((state: RootState) => state.dataset);

  /** Legacy catchment flow: the pre-flag path with POI-count gating and
   * pre-fetched dataset data. When the BVT-for-catchment flag is on we
   * follow the regular add-dataset flow inside the catchment popup.
   */
  const isLegacyCatchmentFlow =
    !!showCatchmentPopup && !isBVTDataForCatchmentEnabled;

  const {
    adminAreaSettings: globalAdminArea,
    selectedAreaIds: globalSelectedAreaIds
  } = useSelector((state: RootState) => state.globalFilter);
  const {
    handleAddDataset,
    handleAddDatasetShowCatchment,
    handleAddOnboardingDataset
  } = useDataset();
  const { showLoading, showError, showSuccess } = useLoadingAndToast();
  const { data: userProfileData } = useGetUserProfileAPI();
  const onboardingMocks = useExploreDatasetMocks();
  const isOnboardingAddActive = useSelector(selectIsOnboardingActive);
  const { mutateAsync: getDatasetMetadata } = useMutationDatasetMetadata({
    meta: {
      isOnboarding: isOnboardingAddActive,
      onboardingMockData: onboardingMocks.datasetMetadata
    }
  });

  const onboardingInitialFilters = useMemo(
    () =>
      isOnboardingAddActive &&
      selectedDataset?.id === onboardingMocks.datasetDetail.id
        ? onboardingMocks.savedFilters
        : [],
    [
      isOnboardingAddActive,
      onboardingMocks.datasetDetail.id,
      onboardingMocks.savedFilters,
      selectedDataset?.id
    ]
  );

  const handleErrorGetDataset = () => {
    if (isLegacyCatchmentFlow) {
      showError(
        <NotificationContent
          title={ERROR_MESSAGE.SOMETHING_WENT_WRONG}
          description={ERROR_MESSAGE.SOMETHING_WENT_WRONG_DESCRIPTION}
        />
      );
    } else {
      showError(
        <NotificationContent title={MESSAGE.DATASET_ADD_TITLE_FAILED}>
          <p className="mt-1">
            {MESSAGE.DATASET_ADD_DESCRIPTION_PREFIX}
            <span className="mx-1 font-semibold">{selectedDataset?.name}</span>
            {MESSAGE.DATASET_ADD_DESCRIPTION_FAILED}
          </p>
        </NotificationContent>
      );
    }
  };

  const { mutateAsync: getDatasetDataPost } = useGetDatasetDataMutation({
    timezone: userProfileData?.timezone,
    options: {
      onError: () => {
        handleErrorGetDataset();
      },
      onSuccess: (data) => {
        setDatasetData(data);
      }
    }
  });
  const { h3Configuration, setH3Configuration } = useH3Configuration({
    selectedDataset
  });
  const {
    isGettingAttributeValues,
    attributeValues,
    defaultSelectedValue,
    filterOptions,
    selectedOptions,
    savedFilters,
    showFilters,
    handleAddAttributeFilter,
    getAvailableFilterOptions,
    initialAdminArea,
    isAttributeFilterValid,
    handleSaveFilter,
    handleEditFilter,
    handleDeleteFilter,
    selectAttributeItem,
    handleSearchChange,
    handleSelectFilter,
    handleAdminAreaChange,
    handleBack,
    searchTerm,
    selectedFilter,
    onFilterChange,
    selectedAdminArea,
    setSelectedAdminArea,
    adminAreaValid,
    isAdminAreaSelection,
    isAdminAreaTreeSelectionValid,
    showAdmAreaTree,
    setShowAdmAreaTree,
    selectedAdminAreaIds,
    setSelectedAdminAreaIds,
    handleSaveAdminAreaTreeSelection,
    isLoadingAdminAreaHierarchy,
    resetAdminAreaIds,
    isOverrideAdminArea,
    setIsOverrideAdminArea,
    isFilterMode,
    isAdminTreeMode,
    isDetailMode,
    isUsingGlobalAdminAreaIds,
    groupByAttributeOptions,
    isGlobalFilterAdminAreaSelected,
    isAdminAreaIdsSameAsGlobal,
    handleSyncSelectionGlobalAdminArea,
    getDataFilterAdminArea,
    onSwitchCustomArea,
    shouldLinkToGlobalFilter
  } = useAttributeFilter({
    selectedDataset,
    initialDatasetFilters: onboardingInitialFilters,
    timezone: userProfileData?.timezone as Timezone,
    isAdminTreeEnabled,
    isGlobalFilterOverrideEnabled
  });
  const { showConfirmation } = useConfirmationModal();

  // Gate the /stats limit check. BVT — and user data that will receive a global
  // admin area (synced after mount) — wait for ≥1 filter, avoiding an extra
  // empty-filter call before the area is applied.
  const isBvtSource = selectedDataset?.source === "bvt";
  const isEnabledQueriesStatusCount =
    !showCatchmentPopup &&
    isDetailMode &&
    !isLoadingAdminAreaHierarchy &&
    ((!isBvtSource && !isGlobalFilterAdminAreaSelected) ||
      savedFilters.length > 0) &&
    isAdminTreeEnabled;

  // The selected dataset measured via `/stats`; memoized to keep the query stable.
  const inputRowsStatsTargets = useMemo(
    () => [
      {
        datasetId: selectedDataset?.id ?? "",
        filters: savedFilters,
        h3Configuration,
        group_by: groupByValue
      }
    ],
    [selectedDataset?.id, savedFilters, h3Configuration, groupByValue]
  );
  const onboardingInputRowsMeta = useMemo(
    () => ({
      isOnboarding: isOnboardingAddActive,
      onboardingMockData: {
        row_count: onboardingMocks.statsCount,
        count_status: DatasetStatsCountStatusENUM.EXACT
      }
    }),
    [isOnboardingAddActive, onboardingMocks.statsCount]
  );

  // Total = selected datasets + this dataset's `/stats` count → drives the
  // warning and the "Add Dataset" disable.
  const {
    isOverLimit: isInputRowsOverLimit,
    limit: inputRowsLimit,
    isFetchingStatusDataset,
    // Stream-side check (H3 can't be measured by `/stats` pre-flight).
    isStreamInputRowLimitExceeded
  } = useSpatialInputRowsLimit({
    statsTargets: inputRowsStatsTargets,
    enabled: isEnabledQueriesStatusCount,
    meta: onboardingInputRowsMeta
  });

  const isInputRowsLimitExceeded = selectedDataset && isInputRowsOverLimit;

  const handleStreamError = () => {
    setIsLoadingDataset(false);
    setDatasetStreamId(undefined);
    handleErrorGetDataset();
  };

  const { mutateAsync: fetchWidgetDefaultConfig } =
    useMutateGetWidgetDefaultConfig({
      meta: {
        isOnboarding: isOnboardingAddActive,
        onboardingMockData: onboardingMocks.widgetDefaultConfig
      }
    });
  useDatasetStream({
    datasetId: datasetStreamId,
    enabled: !!datasetStreamId,
    timezone: userProfileData?.timezone,
    filters: savedFilters,
    h3Configuration,
    group_by: groupByValue,
    onOpen: () => {
      setIsLoadingDataset(true);
      showLoading(
        <NotificationContent description={MESSAGE.DATASET_ADD_LOADING} />
      );
    },
    onStreamComplete: async (data) => {
      if (!selectedDataset) return;
      setIsLoadingDataset(true);
      try {
        const currentDatasetData = data.rawDataset;
        const attributesSchema = data.datasetMetadata;

        if (!currentDatasetData || !attributesSchema) return;
        // Reject datasets in a projected CRS (e.g. UTM Zone 48S / EPSG:32748):
        // the map only supports WGS84 (EPSG:4326), and projected coordinates
        // are meters — far outside lon/lat bounds — so they'd render nowhere.
        if (
          !isFeatureCollectionWGS84(
            convertDatasetResponseToFeatureCollection(
              currentDatasetData,
              attributesSchema.schema
            )
          )
        ) {
          showError(
            <NotificationContent
              title={MESSAGE.DATASET_ADD_DESCRIPTION_UNSUPPORTED_TITLE}
            >
              <p className="mt-1">
                <span className="mx-1 font-semibold">
                  {selectedDataset?.name}
                </span>
                {MESSAGE.DATASET_ADD_DESCRIPTION_UNSUPPORTED_CRS}
              </p>
            </NotificationContent>
          );
          setIsLoadingDataset(false);
          setDatasetStreamId(undefined);
          dispatch(setLoadingOverlay(false));
          return;
        }
        if (
          showCatchmentPopup &&
          catchmentSettingsTemp?.datasetConfig &&
          (isBVTDataForCatchmentEnabled ||
            currentDatasetData.rows.length <= MAX_POI)
        ) {
          /** Mirror the main-dataset path: when the dataset is filtered by an
           * admin area selection that lines up with the global admin area,
           * mark the catchment dataset as global so save flow can echo
           * is_global_admin_area=true and the BE-side global filter applies.
           */
          handleAddDatasetShowCatchment({
            dataset: selectedDataset,
            rawDataset: currentDatasetData,
            attributesSchema: attributesSchema.schema,
            filters: savedFilters,
            is_global_admin_area: isAdminAreaSelection
              ? shouldLinkToGlobalFilter(shouldApplyGlobalAreaRef.current)
              : false
          });
          if (isAdminAreaSelection && shouldApplyGlobalAreaRef.current) {
            await getDataFilterAdminArea();
            shouldApplyGlobalAreaRef.current = false;
          }
        } else {
          // Block if over the limit; catchment is exempt (gated by POI count).
          // A blocked dataset is never committed to the accumulator.
          if (
            !showCatchmentPopup &&
            isStreamInputRowLimitExceeded({
              streamedRows: currentDatasetData?.rows?.length ?? 0
            })
          ) {
            setShowHideLimitWarning(true);
            setIsLoadingDataset(false);
            setDatasetStreamId(undefined);
            dispatch(setLoadingOverlay(false));
            return;
          }
          const newDataset = await handleAddDataset({
            datasetMetadata: selectedDataset,
            datasetData: currentDatasetData,
            savedFilters,
            attributesSchema: attributesSchema.schema,
            h3Configuration,
            group_by: groupByValue,
            index: Object.keys(datasets).length,
            widget: defaultWidgetData?.data.length
              ? defaultWidgetData.data.map((widget) => ({
                  ...widget,
                  is_default: true,
                  label: ""
                }))
              : undefined
          });
          if (isAdminAreaSelection) {
            dispatch(
              toggleUseGlobalFilter({
                datasetId: newDataset.data.id,
                useGlobalFilter: shouldLinkToGlobalFilter(
                  shouldApplyGlobalAreaRef.current
                )
              })
            );

            if (shouldApplyGlobalAreaRef.current) {
              await getDataFilterAdminArea();
              shouldApplyGlobalAreaRef.current = false;
            }
          }
          // The spatial input-rows accumulator is updated inside handleAddDataset.
        }
        if (isLegacyCatchmentFlow && currentDatasetData.rows.length > MAX_POI) {
          setIsLoadingDataset(false);
          return;
        }
        dispatch(setLoadingOverlay(false));
        onClose();
        if (sideBarMenuTypePopupDisplay !== ESidebarMenuType.SPATIAL) {
          dispatch(setSideBarMenuTypePopupDisplay(ESidebarMenuType.SPATIAL));
        }
      } catch {
        return; // catch block only returns here because the error will be handled by the onError above
      }
      // Wait for dataset to render before showing success notification
      showSuccess(
        <NotificationContent title={MESSAGE.DATASET_ADD_TITLE_SUCCESS}>
          <p className="mt-1">
            {MESSAGE.DATASET_ADD_DESCRIPTION_PREFIX}
            <span className="mx-1 font-semibold">{selectedDataset?.name}</span>
            {MESSAGE.DATASET_ADD_DESCRIPTION_SUCCESS}
          </p>
        </NotificationContent>
      );
      setIsLoadingDataset(false);

      setDatasetStreamId(undefined);
    },
    onError: handleStreamError,
    onRetriesExhausted: handleStreamError
  });

  const showAdminAreaSelection = isAdminAreaSelection && isSubscribed;

  const isGlobalFilterAdminAreaSelection =
    isGlobalFilterOverrideEnabled &&
    showAdminAreaSelection &&
    !isGlobalFilterAdminAreaSelected;

  // sync with global admin area filter
  useEffect(() => {
    const isSameCountry =
      selectedDataset?.country_id === globalAdminArea?.lv0?.value;
    if (!showAdminAreaSelection || !isSameCountry) return;
    handleSyncSelectionGlobalAdminArea();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    globalAdminArea,
    filterOptions,
    globalSelectedAreaIds,
    showAdminAreaSelection,
    isLoadingAdminAreaHierarchy,
    selectedDataset
  ]);

  const processAddDataSet = async (shouldApplyGlobal = false) => {
    if (!selectedDataset?.id) return;
    // Onboarding: no SSE/network. Paint the deterministic fixture POI dataset
    // directly (mirrors onStreamComplete's side effects) and close the modal.
    if (isOnboardingAddActive) {
      await handleAddOnboardingDataset({
        datasetDetail: onboardingMocks.datasetDetail,
        datasetData: onboardingMocks.datasetData,
        savedFilters: onboardingMocks.savedFilters,
        attributesSchema: onboardingMocks.datasetMetadata.schema
      });
      onClose();
      return;
    }
    if (isLegacyCatchmentFlow && (datasetData || defaultDatasetData)) {
      const attributesSchema = await getDatasetMetadata({
        id: selectedDataset.id,
        h3Configuration
      });
      const currentDatasetData = datasetData ?? defaultDatasetData;
      if (
        currentDatasetData &&
        currentDatasetData.rows.length <= MAX_POI &&
        catchmentSettingsTemp?.datasetConfig
      ) {
        handleAddDatasetShowCatchment({
          dataset: selectedDataset,
          rawDataset: currentDatasetData,
          attributesSchema: attributesSchema.schema,
          filters: savedFilters,
          is_global_admin_area: isAdminAreaSelection
            ? shouldLinkToGlobalFilter(shouldApplyGlobal)
            : false
        });
        onClose();
        return;
      }
    }
    // Legacy catchment flow skips widget config — its add path doesn't use it.
    if (!isLegacyCatchmentFlow) {
      try {
        // only fetch default widget config for BVT dataset as it's the only dataset with default widget config currently.
        // This is to prevent unnecessary API call for other datasets that don't have default widget config.
        if (selectedDataset.source === "bvt" && isDatasetWidgetEnabled) {
          const defaultWidget = await fetchWidgetDefaultConfig({
            id: selectedDataset.id,
            ...(h3Configuration?.aggregationType && {
              grid_config: {
                aggregation_type: h3Configuration.aggregationType
              }
            }),
            group_by:
              groupByValue && groupByValue === "none" ? "" : groupByValue
          });
          setDefaultWidgetData(defaultWidget ?? undefined);
        }
      } catch {
        // Default widget config is optional — proceed with adding the dataset
        // even if it fails so the user isn't blocked from loading the data.
        setDefaultWidgetData(undefined);
      }
    }
    shouldApplyGlobalAreaRef.current = shouldApplyGlobal;
    setDatasetStreamId(selectedDataset.id);
  };

  const onAddDataset = async () => {
    // During the explore-dataset onboarding the "Add Dataset" click is driven by
    // the tour (step 3 → add-dataset-btn). Skip the global-filter confirmation
    // modal so the deterministic flow isn't interrupted; processAddDataSet's
    // onboarding branch paints the fixture dataset directly.
    if (isGlobalFilterAdminAreaSelection && !isOnboardingAddActive) {
      showConfirmation({
        title: "Confirmation",
        message: MESSAGE.DATASET_CONFIRMATION_CHANGE_GLOBAL_FILTER,
        confirmLabel: "Yes",
        cancelLabel: "No",
        onConfirm: () => onConfirmation(true),
        onCancel: () => onConfirmation(false)
      });
      return;
    }
    await processAddDataSet();
  };

  const onConfirmation = async (value: boolean) => {
    await processAddDataSet(value);
  };

  const onSaveFilter = async () => {
    // Changing the filter changes the row count — let the user retry.
    setShowHideLimitWarning(false);
    const newFilter = await handleSaveFilter();
    if (isLegacyCatchmentFlow && selectedDataset) {
      await getDatasetDataPost({
        id: selectedDataset.id,
        filter: newFilter,
        h3Configuration
      });
    }
  };

  const onDeleteFilter = async (filter: DatasetFilter) => {
    // Removing a filter changes the row count — let the user retry.
    setShowHideLimitWarning(false);
    const filteredSavedFilter = handleDeleteFilter(filter);
    if (isLegacyCatchmentFlow && selectedDataset) {
      await getDatasetDataPost({
        id: selectedDataset.id,
        filter: filteredSavedFilter,
        h3Configuration
      });
    }
  };

  const disabledFilter = disabled || !isSubscribed;
  const showAggregationSelection = selectedDataset && isSubscribed;

  const totalRowsDataset = useMemo(
    () => datasetData?.rows?.length ?? totalRows,
    [datasetData?.rows?.length, totalRows]
  );

  const isInvalidPOILength = useMemo(
    () =>
      isLegacyCatchmentFlow &&
      (totalRowsDataset === 0 || totalRowsDataset > MAX_POI),
    [isLegacyCatchmentFlow, totalRowsDataset]
  );

  const showErrorPOICondition = useMemo(
    () => isLegacyCatchmentFlow && selectedDataset && isInvalidPOILength,
    [isLegacyCatchmentFlow, selectedDataset, isInvalidPOILength]
  );

  const adminTreeConfig = {
    className: "flex-1 min-h-0 max-h-none",
    enabled: isAdminTreeEnabled,
    selectedIds: selectedAdminAreaIds,
    setSelectedIds: (ids: string[]) => {
      setShowHideLimitWarning(false);
      setSelectedAdminAreaIds(ids);
    }
  };

  const shouldShowSaveButton =
    (showFilters && selectedFilter) || isAdminTreeMode;

  const isSaveDisabled = isFilterMode
    ? !isAttributeFilterValid
    : !isAdminAreaTreeSelectionValid || isUsingGlobalAdminAreaIds;

  const handleSave = isFilterMode
    ? onSaveFilter
    : () => {
        handleSaveAdminAreaTreeSelection();
        setIsOverrideAdminArea(
          isGlobalFilterAdminAreaSelected && !isAdminAreaIdsSameAsGlobal
        );
      };

  return (
    <div className="flex flex-col w-[20%] h-full">
      <div
        className="flex flex-col h-[calc(100%-58px)] bg-white-smoke rounded-[12px]"
        data-tourid="dataset-attribute-filter"
      >
        <Header
          showBackButton={showFilters || showAdmAreaTree}
          onBack={() => {
            handleBack();
            if (isAdminTreeMode) resetAdminAreaIds();
          }}
          title={showAdmAreaTree ? "Administrative Area" : "Attribute Filter"}
        />

        <div className="flex flex-col flex-1 min-h-0 px-3 mt-1 pt-0">
          {isFilterMode && (
            <AttributeFilter
              savedFilters={savedFilters}
              getAvailableFilterOptions={getAvailableFilterOptions}
              handleSelectFilter={handleSelectFilter}
              selectAttributeItem={selectAttributeItem}
              defaultSelectedValue={defaultSelectedValue}
              attributeValues={attributeValues}
              selectedOptions={selectedOptions}
              searchTerm={searchTerm}
              isGettingAttributeValues={isGettingAttributeValues}
              onSearchChange={handleSearchChange}
              onChange={onFilterChange}
            />
          )}
          {isAdminTreeMode && (
            <AdminAreaSelection
              selectedData={selectedAdminArea || initialAdminArea}
              onSelectData={() => {}}
              options={{
                showCountryLevel: false,
                showTitle: false
              }}
              treeConfig={adminTreeConfig}
            />
          )}
          {isDetailMode && (
            <>
              <p className="text-xs text-txtBody mb-3 leading-5">
                You may add filters to the attributes based on the dataset you
                select.
              </p>
              <LoadingWrapper
                isLoading={
                  isLoadingAdminAreaHierarchy || isFetchingStatusDataset
                }
              >
                {/* Input-rows limit warning (outside catchment): pre-flight
                    `/stats` over-limit, or the stream-detected over-limit. */}
                {!showCatchmentPopup &&
                  (isInputRowsLimitExceeded || isShowHideLimitWarning) &&
                  !isFetchingStatusDataset && (
                    <LimitWarning
                      className="mb-3"
                      title={MESSAGE.LIMIT_THRESHOLD_WARNING.TITLE}
                      message={MESSAGE.LIMIT_THRESHOLD_WARNING.MESSAGE(
                        inputRowsLimit,
                        "dataset"
                      )}
                    />
                  )}

                {isAdminTreeEnabled && !selectedDataset && (
                  <EmptyState className="flex-grow-0 gap-2" />
                )}

                {isGlobalFilterOverrideEnabled && showAdminAreaSelection && (
                  <CustomFilterArea
                    disabled={!isGlobalFilterAdminAreaSelected}
                    checked={isOverrideAdminArea}
                    onSwitchChange={onSwitchCustomArea}
                  />
                )}

                {isAdminTreeEnabled &&
                  showAdminAreaSelection &&
                  selectedAdminAreaIds.length > 0 && (
                    <SavedAdmArea
                      filter={savedFilters.find(
                        (f) => f.attributeCode === "adm_area_level_1"
                      )}
                      onEdit={() => {
                        setShowAdmAreaTree(true);
                      }}
                      onDelete={() => {
                        const admFilter = savedFilters.find((f) =>
                          f.attributeCode?.includes("adm_area_level")
                        );
                        if (admFilter) handleDeleteFilter(admFilter);
                        setSelectedAdminAreaIds([]);
                      }}
                      disabled={
                        isGlobalFilterAdminAreaSelected && !isOverrideAdminArea
                      }
                    />
                  )}

                {isAdminTreeEnabled && showAdminAreaSelection && (
                  <Divider className="my-3" />
                )}

                {!isAdminTreeEnabled && showAdminAreaSelection && (
                  <AdminAreaSelection
                    disabled={
                      isGlobalFilterAdminAreaSelected && !isOverrideAdminArea
                    }
                    selectedData={selectedAdminArea || initialAdminArea}
                    onSelectData={(newAdminArea) => {
                      setShowHideLimitWarning(false);
                      setSelectedAdminArea(newAdminArea);
                      handleAdminAreaChange(newAdminArea);
                    }}
                    options={{
                      showCountryLevel: false,
                      showLabelAboveInput: false,
                      showLabel: true,
                      prefixLabel: "Select",
                      required: getAdminAreaRequired(selectedDataset),
                      visible: getAdminAreaVisibility(selectedDataset)
                    }}
                  />
                )}

                {isAdminTreeEnabled &&
                  showAdminAreaSelection &&
                  selectedAdminAreaIds.length === 0 && (
                    <Button
                      variant="secondary"
                      className="btn-secondary px-4 font-semibold text-sm mb-3"
                      onClick={() => {
                        setShowAdmAreaTree(true);
                      }}
                    >
                      Adm. Area
                    </Button>
                  )}

                <div className="flex flex-col flex-1 overflow-auto">
                  {showAggregationSelection && (
                    <AggregationFilter
                      onChangeAggregationType={(value) => {
                        setShowHideLimitWarning(false);
                        setH3Configuration((prev) => ({
                          ...prev,
                          aggregationType: value
                        }));
                      }}
                      onChangeGroupBy={(value) => {
                        setShowHideLimitWarning(false);
                        setGroupByValue(value);
                      }}
                      value={h3Configuration}
                      groupByValue={groupByValue}
                      isTimeseries={selectedDataset?.timeseries}
                      openFrom="datasetModal"
                      groupByAttributeOptions={groupByAttributeOptions}
                    />
                  )}
                  {!isUseNewDatasetExplorerModal &&
                    !showAggregationSelection && <EmptyState />}
                  {!isUseNewDatasetExplorerModal && (
                    <Divider className="my-3" />
                  )}
                  {showErrorPOICondition && !isLoading && (
                    <CatchmentErrorMessage datasetLength={totalRowsDataset} />
                  )}
                  <Button
                    variant="secondary"
                    disabled={
                      disabledFilter ||
                      savedFilters.length === filterOptions.length ||
                      !adminAreaValid
                    }
                    className="h-[40px] w-full border-teal-500 bg-white text-teal-500 border text-sm font-bold disabled:border-gainsboro disabled:border disabled:text-gainsboro mb-3"
                    onClick={handleAddAttributeFilter}
                  >
                    {!isAdminTreeEnabled && "Add"} Attribute Filter
                  </Button>
                  {!isAdminTreeEnabled &&
                    isUseNewDatasetExplorerModal &&
                    !showAggregationSelection && <EmptyState />}
                  {/* create saved admin tree */}
                  {savedFilters.length > 0 && (
                    <SavedFiltersList
                      filterOptions={filterOptions}
                      filters={savedFilters}
                      onEdit={handleEditFilter}
                      onDelete={onDeleteFilter}
                    />
                  )}
                </div>
              </LoadingWrapper>
            </>
          )}
        </div>
      </div>
      <div className="mt-4">
        {shouldShowSaveButton ? (
          <Button
            variant="secondary"
            disabled={isSaveDisabled}
            className="w-full h-auto py-2.5 text-sm font-bold text-teal-500 bg-white border-teal-500 disabled:bg-white disabled:text-gainsboro disabled:border disabled:border-gainsboro"
            onClick={handleSave}
            data-testid={`${TEST_ID_PREFIX}-save-button`}
          >
            Save
          </Button>
        ) : (
          !showFilters &&
          !showAdmAreaTree && (
            <Button
              disabled={
                disabledFilter ||
                isLoadingDataset ||
                !adminAreaValid ||
                isInvalidPOILength ||
                isLoadingAdminAreaHierarchy ||
                // Block when over the limit, or hold while `/stats` is counting.
                isInputRowsLimitExceeded ||
                isFetchingStatusDataset ||
                // Block after the stream itself reported an over-limit result.
                isShowHideLimitWarning
              }
              className="w-full h-[40px] py-2.5 border border-teal-500 bg-teal-500 text-white text-sm font-bold disabled:bg-gainsboro disabled:border-none flex-none"
              onClick={onAddDataset}
              data-testid={`${TEST_ID_PREFIX}-add-dataset-button`}
              data-tourid="add-dataset-btn"
            >
              Add Dataset
            </Button>
          )
        )}
      </div>
    </div>
  );
};

export default DatasetRightAttrFilter;
